/* ──────────────────────────────────────────────────────────────
   src/pages/reports/reservas/ReservasMetricasPage.tsx
   ────────────────────────────────────────────────────────────── */
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {
  Stack,
  TextField,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Typography,
  Box,
  Chip,
} from '@mui/material';
/* recharts (alias Tooltip para evitar confusión con MUI) */
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';

/* Hooks de jobs: métricas y tasa de cancelación */
import { useReservasMetricasReport, useCancelacionTasaReport } from '../../../hooks/useReportJob';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#845EC2',
  '#4D8076',
  '#F65A83',
  '#2C73D2',
];

/* ──────────────────────────────────────────────────────────────
   Helpers de formato
   ────────────────────────────────────────────────────────────── */
const fmtInt = (n: number) => (Number.isFinite(n) ? n.toLocaleString() : '—');
const fmt1 = (n: number) =>
  Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—';
const fmtPct1 = (ratio0to1: number) =>
  Number.isFinite(ratio0to1) ? `${(ratio0to1 * 100).toFixed(1)}%` : '—';
const fmtDate = (s?: string) => {
  if (!s) return '—';
  const d = dayjs(s);
  return d.isValid() ? `${d.format('ddd, DD MMM YYYY')}` : s;
};

/* ──────────────────────────────────────────────────────────────
   Tooltips personalizados (Recharts)
   ────────────────────────────────────────────────────────────── */
function LineVolumenTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload ?? {};
  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{fmtDate(label)}</Typography>
      <Typography variant="body2">
        Clases: <b>{fmtInt(p.n)}</b>
      </Typography>
      <Typography variant="body2">
        Horas: <b>{fmt1(p.horas)}</b>
      </Typography>
    </Paper>
  );
}

function PieEstadoTooltip(total: number) {
  return ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0];
    const name = p?.name ?? p?.payload?.estado ?? '—';
    const n = p?.value ?? p?.payload?.n ?? 0;
    const ratio = total > 0 ? n / total : 0;
    return (
      <Paper sx={{ p: 1.5 }}>
        <Typography variant="subtitle2">{name}</Typography>
        <Typography variant="body2">
          {fmtInt(n)} · {fmtPct1(ratio)}
        </Typography>
      </Paper>
    );
  };
}

function BarSimpleTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const nombre = p?.payload?.nombre ?? '—';
  const n = p?.payload?.n ?? 0;
  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" noWrap maxWidth={240}>
        {nombre}
      </Typography>
      <Typography variant="body2">
        Reservas: <b>{fmtInt(n)}</b>
      </Typography>
    </Paper>
  );
}

/* ──────────────────────────────────────────────────────────────
   Card de KPI reutilizable
   ────────────────────────────────────────────────────────────── */
function MetricCard(props: {
  label: string;
  value: string | number;
  caption?: string;
  highlight?: 'primary' | 'success' | 'warning' | 'default';
}) {
  const { label, value, caption, highlight = 'default' } = props;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        minWidth: 180,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background:
          highlight === 'primary'
            ? 'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.03))'
            : highlight === 'success'
              ? 'linear-gradient(135deg, rgba(46,125,50,0.08), rgba(46,125,50,0.03))'
              : highlight === 'warning'
                ? 'linear-gradient(135deg, rgba(237,108,2,0.08), rgba(237,108,2,0.03))'
                : 'transparent',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Paper>
  );
}

export default function ReservasMetricasPage() {
  const { t, i18n } = useTranslation<'translation'>();

  // Helper para forzar string en props que lo requieren
  const tx = (key: string, def: string) => t(key, { defaultValue: def }) as unknown as string;

  // Sincroniza locale de dayjs con i18n
  useEffect(() => {
    dayjs.locale(i18n.language || 'es');
  }, [i18n.language]);

  /* ───── filtros ───── */
  const hoy = dayjs().format('YYYY-MM-DD');
  const [desde, setDesde] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(hoy);
  const [top, setTop] = useState(10);

  const invalidRange = useMemo(() => dayjs(desde).isAfter(dayjs(hasta)), [desde, hasta]);

  /* ───── cola + polling (dos jobs en paralelo) ───── */
  const { run: runMetricas, useStatus: useStatusMet } = useReservasMetricasReport();
  const { run: runCancel, useStatus: useStatusCan } = useCancelacionTasaReport();

  const [jobIdMet, setJobIdMet] = useState<string>();
  const [jobIdCan, setJobIdCan] = useState<string>();

  const { data: status } = useStatusMet(jobIdMet);
  const { data: statusCan } = useStatusCan(jobIdCan);

  const generar = async () => {
    if (invalidRange) return;
    const [{ jobId: jm }, { jobId: jc }] = await Promise.all([
      runMetricas({ desde, hasta, top }),
      runCancel({ desde, hasta }),
    ]);
    setJobIdMet(jm);
    setJobIdCan(jc);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  const handleDownloadCan = async () => {
    if (statusCan?.result?.url) {
      try {
        await downloadReportFile(statusCan.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  /* ───── defensivo: siempre arrays/objetos válidos ───── */
  const vol = status?.result?.volumenDiario ?? [];
  const porEstado = status?.result?.estado ?? [];
  const topAlu = status?.result?.topAlumnos ?? [];
  const topProf = status?.result?.topProfesores ?? [];
  const topCent = status?.result?.topCentros ?? [];
  const leadTime = status?.result?.leadTime ?? { avg: 0, min: 0, max: 0 };
  const antiguedad = status?.result?.antiguedad ?? {
    abiertas: 0,
    ejecutadas: 0,
  };

  /* ───── KPIs derivados ───── */
  const { totalClases, totalHoras, mediaDia, pico, cancelPct, cancelAluPct, cancelIntPct } =
    useMemo(() => {
      const totalClases = vol.reduce((acc: number, r: any) => acc + (r?.n ?? 0), 0);
      const totalHoras = vol.reduce((acc: number, r: any) => acc + (r?.horas ?? 0), 0);
      const diasN = vol.length || 0;
      const mediaDia = diasN ? totalClases / diasN : 0;

      const pico = vol.reduce((max: any, r: any) => (r?.n > max?.n ? r : max), {
        fecha: '—',
        n: 0,
        horas: 0,
      });

      const can = statusCan?.result;
      const cancelPct = can ? can.cancelPct : 0;
      const cancelAluPct = can ? can.cancelAluPct : 0;
      const cancelIntPct = can ? can.cancelIntPct : 0;

      return {
        totalClases,
        totalHoras,
        mediaDia,
        pico,
        cancelPct,
        cancelAluPct,
        cancelIntPct,
      };
    }, [vol, statusCan?.result]);

  const isRunning =
    (!!status && !status.done && !status.failed) ||
    (!!statusCan && !statusCan.done && !statusCan.failed);

  const hasError = (status && status.failed) || (statusCan && statusCan.failed);

  /* total para tooltip del pie */
  const totalPorEstado = porEstado.reduce((s: number, r: any) => s + (r?.n ?? 0), 0);

  return (
    <Stack spacing={3}>
      <Typography variant="h5">
        {tx('reservasMetricas.titulo', 'Métricas globales de reservas')}
      </Typography>

      {/* filtros */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          label={tx('reservasMetricas.filtros.desde', 'Desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          size="small"
        />
        <TextField
          type="date"
          label={tx('reservasMetricas.filtros.hasta', 'Hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          size="small"
        />
        <TextField
          type="number"
          label={tx('reservasMetricas.filtros.top', 'Top')}
          value={top}
          sx={{ width: 120 }}
          onChange={(e) => setTop(Math.max(+e.target.value || 1, 1))}
          size="small"
        />
        <Button variant="contained" onClick={generar} disabled={isRunning || invalidRange}>
          {tx('reservasMetricas.filtros.generar', 'Generar')}
        </Button>
        {invalidRange && (
          <Chip
            color="warning"
            label={tx('reservasMetricas.filtros.rangoInvalido', 'Rango inválido')}
            size="small"
          />
        )}
      </Stack>

      {/* progreso */}
      {isRunning && (
        <Stack spacing={1}>
          <LinearProgress variant="indeterminate" />
          <Typography variant="body2">
            {tx('reservasMetricas.estado.generando', 'Generando informe…')}
          </Typography>
        </Stack>
      )}

      {hasError && (
        <Alert severity="error">
          {tx('reservasMetricas.estado.error', 'Ha ocurrido un error generando el informe.')}
        </Alert>
      )}

      {/* resultado */}
      {status?.done && status.result && (
        <>
          <Alert severity="success">
            {tx('reservasMetricas.estado.exito', 'Informe generado.')}{' '}
            <Button
              variant="text"
              size="small"
              onClick={handleDownload}
              sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
            >
              {tx('reservasMetricas.estado.descargar', 'Descargar métricas')}
            </Button>
            {statusCan?.result?.url && (
              <>
                {'  ·  '}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleDownloadCan}
                  sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
                >
                  {tx('reservasMetricas.estado.descargarCancel', 'Descargar cancelación')}
                </Button>
              </>
            )}
          </Alert>

          {/* KPIs */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <MetricCard
              label={tx('reservasMetricas.kpi.totalClases', 'Clases')}
              value={fmtInt(totalClases)}
              highlight="primary"
              caption={`${tx('reservasMetricas.kpi.rango', 'Rango')} · ${fmtDate(desde)} → ${fmtDate(hasta)}`}
            />
            <MetricCard
              label={tx('reservasMetricas.kpi.totalHoras', 'Horas')}
              value={fmt1(totalHoras)}
              highlight="primary"
            />
            <MetricCard
              label={tx('reservasMetricas.kpi.mediaDia', 'Media/día')}
              value={fmt1(mediaDia)}
              caption={tx('reservasMetricas.kpi.soloDias', 'Solo días con datos')}
            />
            <MetricCard
              label={tx('reservasMetricas.kpi.diaPico', 'Día pico')}
              value={pico?.fecha ? `${fmtDate(pico.fecha)}` : '—'}
              caption={`${tx('reservasMetricas.kpi.clases', 'Clases')}: ${fmtInt(pico?.n ?? 0)}`}
              highlight="success"
            />
            <MetricCard
              label={tx('reservasMetricas.kpi.cancelacion', 'Cancelación')}
              value={fmtPct1(cancelPct)}
              caption={`${tx('reservasMetricas.kpi.alu', 'Alumno')}: ${fmtPct1(cancelAluPct)} · ${tx('reservasMetricas.kpi.int', 'Interna')}: ${fmtPct1(cancelIntPct)}`}
              highlight="warning"
            />
            <MetricCard
              label={tx('reservasMetricas.kpi.leadTime', 'Lead-time')}
              value={`${fmt1(leadTime.avg)} ${tx('common.dias', 'días')}`}
              caption={`min ${fmtInt(leadTime.min)} · max ${fmtInt(leadTime.max)}`}
            />
            <MetricCard
              label={tx('reservasMetricas.kpi.pipeline', 'Pipeline')}
              value={`${fmtInt(antiguedad.abiertas)} ${tx('reservasMetricas.kpi.abiertas', 'abiertas')}`}
              caption={`${tx('reservasMetricas.kpi.ejecutadas', 'ejecutadas')}: ${fmtInt(antiguedad.ejecutadas)}`}
            />
          </Stack>

          {/* volumen diario */}
          <Paper sx={{ p: 2, height: 340 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">
                {tx('reservasMetricas.volumen.titulo', 'Volumen diario')}
              </Typography>
              <Box>
                <Chip
                  size="small"
                  label={tx('reservasMetricas.kpi.clases', 'Clases')}
                  sx={{ mr: 1 }}
                />
                <Chip
                  size="small"
                  label={tx('reservasMetricas.kpi.horas', 'Horas')}
                  variant="outlined"
                />
              </Box>
            </Stack>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={vol}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="fecha" hide />
                <YAxis allowDecimals={false} />
                <RechartsTooltip content={<LineVolumenTooltip />} />
                <Line
                  type="monotone"
                  dataKey="n"
                  stroke="#1976d2"
                  dot={false}
                  isAnimationActive={false}
                  name={tx('reservasMetricas.kpi.clases', 'Clases')}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* por estado */}
          <Paper sx={{ p: 2, height: 340 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {tx('reservasMetricas.estado.titulo', 'Distribución por estado')}
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={porEstado} dataKey="n" nameKey="estado" label isAnimationActive={false}>
                  {porEstado.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip content={PieEstadoTooltip(totalPorEstado)} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>

          {/* lead-time / antigüedad */}
          <Paper sx={{ p: 2 }}>
            <Typography>
              <b>{tx('reservasMetricas.kpi.leadTime', 'Lead-time')}:</b> {fmt1(leadTime.avg)}{' '}
              {tx('common.dias', 'días')}{' '}
              <Typography variant="caption" component="span">
                ({tx('common.min', 'min')} {fmtInt(leadTime.min)}, {tx('common.max', 'max')}{' '}
                {fmtInt(leadTime.max)})
              </Typography>
              {'  '}•{'  '}
              <b>{tx('reservasMetricas.kpi.abiertas', 'abiertas')}:</b>{' '}
              {fmtInt(antiguedad.abiertas)} |{' '}
              <b>{tx('reservasMetricas.kpi.ejecutadas', 'ejecutadas')}:</b>{' '}
              {fmtInt(antiguedad.ejecutadas)}
            </Typography>
          </Paper>

          {/* TOP N  */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {/* alumnos */}
            <Paper sx={{ p: 2, flex: 1, height: 340 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {tx('reservasMetricas.top.alumnos', 'TOP alumnos')}
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topAlu}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nombre" hide />
                  <YAxis allowDecimals={false} />
                  <Legend />
                  <RechartsTooltip content={<BarSimpleTooltip />} />
                  <Bar
                    dataKey="n"
                    name={tx('reservasMetricas.chart.label', 'Reservas')}
                    barSize={24}
                    isAnimationActive={false}
                  >
                    {topAlu.map((_: any, i: number) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>

            {/* profesores */}
            <Paper sx={{ p: 2, flex: 1, height: 340 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {tx('reservasMetricas.top.profesores', 'TOP profesores')}
              </Typography>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topProf}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nombre" hide />
                  <YAxis allowDecimals={false} />
                  <Legend />
                  <RechartsTooltip content={<BarSimpleTooltip />} />
                  <Bar
                    dataKey="n"
                    name={tx('reservasMetricas.chart.label', 'Reservas')}
                    barSize={24}
                    isAnimationActive={false}
                  >
                    {topProf.map((_: any, i: number) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Stack>

          {/* TOP Centros */}
          <Paper sx={{ p: 2, height: 340 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {tx('reservasMetricas.top.centros', 'TOP centros')}
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topCent}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nombre" hide />
                <YAxis allowDecimals={false} />
                <Legend />
                <RechartsTooltip content={<BarSimpleTooltip />} />
                <Bar
                  dataKey="n"
                  name={tx('reservasMetricas.chart.label', 'Reservas')}
                  barSize={24}
                  isAnimationActive={false}
                >
                  {topCent.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}
    </Stack>
  );
}
